/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {MinusIcon, PlusIcon} from "@heroicons/react/24/outline";

interface BookingPackageItemsWithKartingCountInputProps {
    setKarting: (kartingCount: number) => void;
    karting: number;
    label: string;
    maxCount: number;
}

const BookingPackageItemsWithKartingCountInput = (props: BookingPackageItemsWithKartingCountInputProps) => {
    const {t} = useTranslation();
    const [karting, setKarting] = useState<number>(props?.karting ? props.karting : 0);

    const setKartingCountHandler = (count: number) => {
        const newCount: number = props.karting + count;
        if (newCount > props.maxCount || newCount < 0) {
            return;
        }
        setKarting(newCount);
        props.setKarting(newCount);
    };

    const setKartingInputHandler = (count: string) => {
        if (+count > props.maxCount || +count < 0) {
            return;
        }
        props.setKarting(+count);
    };

    useEffect(() => {
        setKarting(props.karting);
    }, [props.karting]);

    return (
        <div
            className={clsx('BookingPackageItemsWithKartingCountInput flex flex-col items-center justify-center min-w-30 max-w-30 sm:max-w-36 w-full')}
        >
            <div className={clsx(
                'font-medium text-sm border border-b-0 rounded-tl rounded-tr px-2 py-1.5 w-full text-center grow transition-colors',
                karting > 0
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
            )}>{props.label}</div>
            <div className={clsx('flex flex-row items-center justify-center w-full')}>
                <button
                    className={clsx(
                        "rounded-bl flex flex-row justify-center items-center px-2 py-2 font-bold text-lg leading-none min-w-10 sm:min-w-11 min-h-10 border transition-colors",
                        karting > 0
                            ? 'border-blue-300 dark:border-blue-700'
                            : 'border-gray-200 dark:border-gray-700'
                    )}
                    type={"button"}
                    onClick={() => setKartingCountHandler(-1)}
                >
                    <MinusIcon className={clsx('pointer-events-none w-5 h-5')} />
                </button>
                <input
                    className={clsx(
                        "border-t border-b flex h-full min-h-10  min-w-10  w-full px-1 text-center font-medium placeholder-black  hover:appearance-none transition-colors",
                        karting > 0
                            ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-bold'
                            : 'border-gray-200 dark:border-gray-700'
                    )}
                    inputMode={"numeric"}
                    max={props.maxCount}
                    min={0}
                    name={"withKarting"}
                    type={"text"}
                    value={karting}
                    onChange={e => setKartingInputHandler(e.target.value)}
                />
                <button
                    className={clsx(
                        "rounded-br flex flex-row justify-center items-center px-2 py-2 font-bold text-lg leading-none min-w-10 sm:min-w-11 min-h-10 border transition-colors",
                        karting > 0
                            ? 'border-blue-300 dark:border-blue-700'
                            : 'border-gray-200 dark:border-gray-700'
                    )}
                    type={"button"}
                    onClick={() => setKartingCountHandler(+1)}
                >
                    <PlusIcon className={clsx('pointer-events-none w-5 h-5')} />
                </button>
            </div>
        </div>
    );
};

export default BookingPackageItemsWithKartingCountInput;
